import toggleVisibility from '../../utils/toggleVisibility';
import { debounce } from 'throttle-debounce';
import { ARIAS, CLASSES } from '../../utils/constants';

/**
 * Short Description (Read More / Read Less)
 *
 * Compares scrollHeight vs clientHeight to decide whether the content is
 * clamped. A ResizeObserver re-checks on container resize (debounced).
 * Both the ResizeObserver and a MutationObserver are disconnected
 * automatically when the wrapper is removed from the DOM.
 */

const DEBOUNCE_RESIZE_DELAY = 150;
const COLLAPSED_CLASSES = ['line-clamp-4', 'lg:line-clamp-3'];

const initProductShortDescription = () => {
  const wrapperEls = document.querySelectorAll('[data-product-short-description]');
  if (!wrapperEls.length) return;

  wrapperEls.forEach((wrapper) => {
    if (wrapper.dataset.shortDescriptionInitialised === 'true') return;

    const contentEl = wrapper.querySelector('[data-product-short-description-content]');
    const toggleEl = wrapper.querySelector('[data-product-short-description-toggle]');
    if (!contentEl || !toggleEl) return;

    const readMoreLabel = toggleEl.textContent?.trim() || 'Read more';
    const readLessLabel = toggleEl.dataset.readLessLabel || 'Read less';

    /** Apply or remove the line-clamp classes. */
    const setExpandedState = (isExpanded) => {
      contentEl.classList.toggle(CLASSES.LINE_CLAMP_NONE, isExpanded);
      contentEl.classList.toggle(CLASSES.OVERFLOW_VISIBLE, isExpanded);
      COLLAPSED_CLASSES.forEach((cls) => contentEl.classList.toggle(cls, !isExpanded));
    };

    /** Sync the toggle button label and aria-expanded. */
    const updateToggleState = () => {
      const isExpanded = contentEl.classList.contains(CLASSES.LINE_CLAMP_NONE);
      toggleEl.textContent = isExpanded ? readLessLabel : readMoreLabel;
      toggleEl.setAttribute(ARIAS.EXPANDED, isExpanded ? 'true' : 'false');
    };

    /** When line-clamp is active, scrollHeight exceeds clientHeight if text overflows. */
    const shouldShowToggle = () => contentEl.scrollHeight > contentEl.clientHeight + 1;

    /** Show or hide the toggle button based on whether content is clamped. */
    const setToggleVisibility = () => {
      if (contentEl.classList.contains(CLASSES.LINE_CLAMP_NONE)) return;

      if (shouldShowToggle()) {
        toggleVisibility(toggleEl, true, 'inline-flex');
        updateToggleState();
      } else {
        toggleVisibility(toggleEl, false, 'inline-flex');
      }
    };

    // Collapse by default
    setExpandedState(false);

    // Wait for fonts before first measurement so line-height is accurate
    const syncAfterFonts = async () => {
      try {
        await document.fonts.ready;
      } catch {
        // Font loading failed — fall through and measure with whatever rendered.
      }
      setToggleVisibility();
    };
    syncAfterFonts();

    // Re-check on container resize (e.g. viewport change, sidebar toggle)
    const resizeObserver = new ResizeObserver(debounce(DEBOUNCE_RESIZE_DELAY, () => {
      setToggleVisibility();
    }));
    resizeObserver.observe(wrapper);

    toggleEl.addEventListener('click', () => {
      setExpandedState(!contentEl.classList.contains(CLASSES.LINE_CLAMP_NONE));
      updateToggleState();
    });

    // Disconnect observers when the wrapper is removed from the DOM (section reload).
    // Observes document.body with subtree so removal of any ancestor is caught.
    const mutationObserver = new MutationObserver(() => {
      if (!wrapper.isConnected) {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    wrapper.dataset.shortDescriptionInitialised = 'true';
  });
};

export default initProductShortDescription;
